import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { axiosFunction, toastMessage } from "../../components/commonFunctions";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { v2ReactURL } from "../../utils/constant";
import { FaCheckSquare } from "react-icons/fa";
import { IoIosWarning } from "react-icons/io";
import { colors } from "@mui/material";

// Types (updated for separate sales_id handling)
interface Driver {
  employeeId: number;
  employeeName: string;
  mobileNo: string;
  userFrontId: number | null;
  accountCustomId: number;
  vehicleNo: string;
}

interface DeliverySlot {
  deliverySlotId: number;
  deliverySlotName: string;
  startTime: string;
  endTime: string;
  calculatedCutOffTime: string;
  maximumOrders: number;
  isActive: number;
}

interface SalesItem {
  sales_id: number;
  name: string;
  product_id: number;
  qty: number;
  order_by: number;
  varient_name: string;
  price: number;
  mrp: number;
  product_varient_id: number;
  salesitemid: number;
}

interface SalesOrder {
  sales_id: number;
  prefix: string;
  sales_no: number;
  fullname: string;
  pincode: string;
  phoneno: string | null;
  customerid: number | null;
  status: string;
  payment_status: string;
  checked: number;
  salesItems: SalesItem[];
}

interface InventoryItem {
  item_code: string;
  salesId: string;
  orderno: string;
  productVarientsVo: {
    varientName: string;
    productVo: {
      displayName: string;
      brandVo: {
        brandName: string;
      };
    };
  };
  qty: number;
  mrp?: number;
  price?: number;
  tax_name?: string;
  batch_no?: string;
  scannedQty?: number;
  verified?: boolean;
  product_varient_id?: number;
  sales_id?: number; // Added for payload grouping
}

interface FormData {
  recipeId: number;
  taxType: number;
  total: number;
  saveandpayment: 0;
  saveandnew: 0;
  saveandprint: 0;
  contactVo: { contactId: string };
  deliveryDate: string;
  prefix: string;
  deliveryNo: string;
  vehicleId: string;
  deliverySlotId: string;
  recipeParentItemVos: any[];
  recipeItemVos: any[];
}

interface DeliverySheetApiResponse {
  status: string;
  driverName: string;
  contactId: number;
  createdBy: string;
  deliveryAssignId: number;
  deliverySlotId: number;
  deliveryDate: string;
  deliverySlotName: string;
  vehicleNo: string;
}

interface FullApiResponse {
  deliveryOrders: SalesOrder[];
  deliverySheet: DeliverySheetApiResponse;
}

interface ProductSearchItem {
  name: string;
  id: number;
  text: string;
  mrp: number;
  qty: string;
  itemCode: string;
  customerMapping: number;
  product_varient_id?: number;
}

interface ProductSearchResponse {
  status: boolean;
  message: string;
  response: {
    total_count: number;
    incomplete_results: boolean;
    items: ProductSearchItem[];
  };
}

interface ScannedQuantityItem {
  productVarientId: number;
  expectedQty: number;
  scannedQty: number;
  salesId: number;
}

interface DeliveryPayload {
  salesIds: number[];
  deliveryDate: string;
  contactId: number;
  vehicleNo: string;
  deliverySlotId: string;
  deliveryAssignId: number;
  scannedQuantities: ScannedQuantityItem[];
}

const DeliveryForm = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = localStorage.getItem('IsEdit') === "True"; 
  const isViewMode = localStorage.getItem('IsView') === "True";
  
  const [formData, setFormData] = useState<FormData>({
    recipeId: 0,
    taxType: 0,
    total: 0,
    saveandpayment: 0,
    saveandnew: 0,
    saveandprint: 0,
    contactVo: { contactId: "" },
    deliveryDate: "",
    prefix: "DEL",
    deliveryNo: "",
    vehicleId: "",
    deliverySlotId: "",
    recipeParentItemVos: [],
    recipeItemVos: [],
  });
  
  const [deliveryDate, setDeliveryDate] = useState<Dayjs | null>(dayjs());
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [switchProductScanning] = useState<number>(1);
  const [vehicleNo, setVehicleNo] = useState<string>("");
  const [deliverySlots, setDeliverySlots] = useState<DeliverySlot[]>([]);
  const [salesOrders, setSalesOrders] = useState<SalesOrder[]>([]);
  const [selectedOrders, setSelectedOrders] = useState<SalesOrder[]>([]);
  const [inventoryData, setInventoryData] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState({
    drivers: false,
    slots: false,
    orders: false,
    inventory: false,
    editData: false,
    productSearch: false
  });
  //  console.log("inventoryData--->",inventoryData)
  // Scanner functionality states
  const [scannerInput, setScannerInput] = useState<string>("");
  const scannerInputRef = useRef<HTMLInputElement>(null);
  const scannerTimeoutRef = useRef<NodeJS.Timeout>();

  const [displayType] = useState("Delivery");
  const [type] = useState("delivery");
  const [selectAllOrders, setSelectAllOrders] = useState(false);

  const isDisabled = isViewMode || (isEditMode && loading.editData);

  // Focus management
  useEffect(() => {
    if (scannerInputRef.current && !isViewMode) {
      scannerInputRef.current.focus();
    }
  }, [isViewMode, inventoryData, selectedOrders]);

  useEffect(() => {
    return () => {
      if (scannerTimeoutRef.current) {
        clearTimeout(scannerTimeoutRef.current);
      }
    };
  }, []);

  // Fetch drivers and delivery slots on component mount
  useEffect(() => {
    fetchDrivers();
    fetchDeliverySlots();
    
    const formattedDate = dayjs().format('DD/MM/YYYY');
    setFormData((prev) => ({ ...prev, deliveryDate: formattedDate }));

    if (isEditMode || isViewMode) {
      fetchDeliverySheetData();
    }
  }, [isEditMode, isViewMode, id]);

  // Auto-fetch sales orders when all required fields are filled
  useEffect(() => {
  
    if (formData.contactVo.contactId && formData.deliverySlotId && formData.deliveryDate && !isEditMode && !isViewMode) {
      getSalesData();
    }
  }, [formData.contactVo.contactId, formData.deliverySlotId, formData.deliveryDate]);
  //  console.log("ddddddddddddddddddddd",inventoryData)
  const handleScannerInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isViewMode) return;

    const value = e.target.value;
    setScannerInput(value);

    if (scannerTimeoutRef.current) {
      clearTimeout(scannerTimeoutRef.current);
    }

    scannerTimeoutRef.current = setTimeout(() => {
      if (value.trim()) {
        handleScannedItemCode(value.trim());
      }
      setScannerInput("");
    }, 100);
  };
  
  // Process scanned item code - UPDATED TO SCAN BY SALES_ID
  const handleScannedItemCode = async (itemCode: string) => {
    if (!itemCode || loading.productSearch) return;

    setLoading(prev => ({ ...prev, productSearch: true }));
    try {
      const response = await axiosFunction(
        `${v2ReactURL}/native/delivery/productlist`,
        "POST",
        { searchItemcode: itemCode },
        false
      );

      const apiResponse: ProductSearchResponse = response?.data;

      if (!apiResponse) {
        toastMessage("No response from server. Please try again.");
        return;
      }

      if (!apiResponse.status) {
        toastMessage(apiResponse.message || "Failed to search product");
        return;
      }

      if (!apiResponse.response?.items?.length) {
        toastMessage("Product not found with this item code");
        return;
      }

      const product = apiResponse.response.items[0];

      if (!product) {
        toastMessage("Product not found");
        return;
      }

      // Find ALL inventory items with this item code that are NOT fully verified
      const matchingItems = inventoryData.filter(
        item => item.item_code === product.itemCode && !item.verified
      );

      if (matchingItems.length === 0) {
        // Check if product exists but is already verified
        const existingVerifiedItems = inventoryData.filter(
          item => item.item_code === product.itemCode && item.verified
        );
        
        if (existingVerifiedItems.length > 0) {
          toastMessage(`Item "${product.name}" is already fully verified`);
        } else {
          toastMessage("Product not found in selected orders. Please select orders containing this product first.");
        }
        return;
      }

      // Update inventory data - increment the FIRST unverified matching item
      if (matchingItems.length > 0) {
        const firstMatching = matchingItems[0];

        const updatedInventoryData = inventoryData.map(item => {
          // Match by both item_code AND sales_id to ensure we're updating the correct product instance
          if (item.item_code === product.itemCode && 
              item.sales_id === firstMatching?.sales_id && 
              !item.verified) {
            
            const newScannedQty = (item.scannedQty || 0) + 1;
            
            // PREVENT SCANNING MORE THAN TOTAL QUANTITY
            if (newScannedQty > item.qty) {
              toastMessage(`Cannot scan more than available quantity (${item.qty}) for ${product.name}`);
              return item;
            }
            
            const isNowVerified = newScannedQty === item.qty;
            
            return {
              ...item,
              scannedQty: newScannedQty,
              verified: isNowVerified
            };
          }
          return item;
        });
        
        setInventoryData(updatedInventoryData);
        
        // Show scanning feedback
        const updatedItem = updatedInventoryData.find(
          item => item.item_code === product.itemCode && 
                  item.sales_id === firstMatching?.sales_id
        );
        
        if (updatedItem && !updatedItem.verified) {
          // Show scanning progress
          toastMessage(`Scanned: ${product.name} (${updatedItem.scannedQty}/${updatedItem.qty}) - Order #${updatedItem.sales_id}`);
        }
      }
    } catch (error) {
      console.error("Error searching product:", error);
      toastMessage("Error searching product. Please try again.");
    } finally {
      setLoading(prev => ({ ...prev, productSearch: false }));
      // Re-focus after processing
      if (scannerInputRef.current) {
        setTimeout(() => {
          if (scannerInputRef.current) {
            scannerInputRef.current.focus();
          }
        }, 100);
      }
    }
  };

  // Manual search function
  const handleManualSearch = async () => {
    if (isViewMode || !scannerInput.trim()) {
      toastMessage("Please enter an item code");
      return;
    }

    await handleScannedItemCode(scannerInput.trim());
    setScannerInput("");
  };

  // Handle direct quantity input - UPDATED FOR SEPARATE SALES_ID HANDLING
  const handleInventoryQuantityInput = (itemCode: string, salesId: number, newQty: number) => {
    if (isViewMode || newQty < 0) return;
    
    const item = inventoryData.find(item => item.item_code === itemCode && item.sales_id === salesId);
    if (!item) return;
    
    // PREVENT ENTERING MORE THAN TOTAL QUANTITY
    if (newQty > item.qty) {
      toastMessage(`Cannot enter more than available quantity (${item.qty})`);
      return;
    }
    
    const updatedInventoryData = inventoryData.map(invItem => {
      if (invItem.item_code === itemCode && invItem.sales_id === salesId) {
        const isNowVerified = newQty === invItem.qty;
        
        return {
          ...invItem,
          scannedQty: newQty,
          verified: isNowVerified
        };
      }
      return invItem;
    });
    
    setInventoryData(updatedInventoryData);
  };

  // Fetch delivery sheet data for edit/view mode
  const fetchDeliverySheetData = async () => {
    setLoading(prev => ({ ...prev, editData: true }));
    
    try {
      const response = await axiosFunction(
        `${v2ReactURL}/native/delivery/getDeliverySheetById/${localStorage.getItem('deliverySheetId')}`,
        "GET",
        false
      );

      if (response?.data?.status && response.data.response) {
        const deliveryData: FullApiResponse = response.data.response;
        const deliverySheet = deliveryData.deliverySheet;
        const deliveryOrders = deliveryData.deliveryOrders;
        
        setVehicleNo(deliverySheet.vehicleNo || "");
        
        if (deliverySheet.contactId) {
          setFormData(prev => ({ 
            ...prev, 
            contactVo: { contactId: deliverySheet.contactId.toString() } 
          }));
        }
        
        if (deliverySheet.deliveryDate) {
          const dateParts = deliverySheet.deliveryDate.split('-');
          const formattedDate = `${dateParts[2]}/${dateParts[1]}/${dateParts[0]}`;
          const date = dayjs(deliverySheet.deliveryDate);
          setDeliveryDate(date);
          setFormData(prev => ({ ...prev, deliveryDate: formattedDate }));
        }
        
        if (deliverySheet.deliverySlotId) {
          setFormData(prev => ({ ...prev, deliverySlotId: deliverySheet.deliverySlotId.toString() }));
        }

        const ordersWithOriginalState = deliveryOrders.map(order => ({
          ...order,
          checked: order.checked || 0
        }));
        
        setSalesOrders(ordersWithOriginalState);

        const initiallySelectedOrders = ordersWithOriginalState.filter(order => order.checked === 1);
        setSelectedOrders(initiallySelectedOrders);

        const allChecked = ordersWithOriginalState.every(order => order.checked === 1);
        setSelectAllOrders(allChecked);

        initiallySelectedOrders.forEach(order => {
          callBarcodeAPI(order.sales_id.toString());
        });

        localStorage.setItem(`deliverySheet_${id}`, JSON.stringify({
          deliverySheet,
          deliveryOrders: ordersWithOriginalState,
          timestamp: new Date().getTime()
        }));

      } else {
        console.error("Invalid API response format for edit/view:", response?.data);
        toastMessage("Failed to load delivery sheet data.");
      }
    } catch (error) {
      console.error("Error fetching delivery sheet data:", error);
      
      try {
        const cachedData = localStorage.getItem(`deliverySheet_${id}`);
        if (cachedData) {
          const { deliveryOrders } = JSON.parse(cachedData);
          
          setSalesOrders(deliveryOrders);
          
          const selectedFromCache = deliveryOrders.filter((order: SalesOrder) => order.checked === 1);
          setSelectedOrders(selectedFromCache);
          
          const allCheckedFromCache = deliveryOrders.every((order: SalesOrder) => order.checked === 1);
          setSelectAllOrders(allCheckedFromCache);
          
          toastMessage("Loaded data from cache");
        }
      } catch (cacheError) {
        console.error("Error loading from cache:", cacheError);
      }
      
      toastMessage("Error loading delivery sheet data");
    } finally {
      setLoading(prev => ({ ...prev, editData: false }));
    }
  };

  // Handle date change from DatePicker
  const handleDateChange = (newDate: Dayjs | null) => {
    if (isViewMode) return;
    
    setDeliveryDate(newDate);
    
    if (newDate) {
      const formattedDate = newDate.format('DD/MM/YYYY');
      setFormData((prev) => ({ ...prev, deliveryDate: formattedDate }));
    }
  };

  // Fetch drivers list from API
  const fetchDrivers = async () => {
    setLoading(prev => ({ ...prev, drivers: true }));
    try {
      const response = await axiosFunction(
        `${v2ReactURL}/native/delivery/driverList`,
        "POST",
        false
      );
      if (response?.data?.status) {
        setDrivers( response.data.response.employeeList );
        console.log("drivers list ---->", response.data.response.employeeList[0]);
        if (!isEditMode && !isViewMode && response.data.response.length > 0 && response.data.response[0].vehicleNo) {
          setVehicleNo(response.data.response[0].vehicleNo);
          setFormData(prev => ({ ...prev, vehicleId: response.data.response[0].vehicleNo }));
        }
      } else {
        setDrivers([]);
      }
    } catch (error) {
      console.error("Error fetching drivers:", error);
      setDrivers([]);
    } finally {
      setLoading(prev => ({ ...prev, drivers: false }));
    }
  };

  // Fetch delivery slots
  const fetchDeliverySlots = async () => {
    setLoading(prev => ({ ...prev, slots: true }));
    try {
      const response = await axiosFunction(
        `${v2ReactURL}/native/delivery/deliverySlotList`,
        "GET"
      );
      if (response?.data?.status && response.data.response) {
        setDeliverySlots(response.data.response);
      }
    } catch (error) {
      console.error("Error fetching delivery slots:", error);
    } finally {
      setLoading(prev => ({ ...prev, slots: false }));
    }
  };

  // Handle driver selection
  const handleDriverChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    if (isViewMode) return;
    
    const contactId = e.target.value;
    const selectedDriver = drivers.find(driver => driver.employeeId.toString() === contactId);
    
    setFormData((prev) => ({
      ...prev,
      contactVo: { ...prev.contactVo, contactId },
    }));

    if (selectedDriver && selectedDriver.vehicleNo) {
      setVehicleNo(selectedDriver.vehicleNo);
      setFormData(prev => ({ ...prev, vehicleId: selectedDriver.vehicleNo }));
    } else {
      setVehicleNo("");
      setFormData(prev => ({ ...prev, vehicleId: "" }));
    }
  };

  // Handle delivery slot selection
  const handleDeliverySlotChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    if (isViewMode) return;
    
    const deliverySlotId = e.target.value;
    setFormData((prev) => ({ ...prev, deliverySlotId }));
  };

  // Fetch sales data
  const getSalesData = async () => {
    if (!formData.contactVo.contactId || !formData.deliveryDate || !formData.deliverySlotId) {
      return;
    }
    // debugger
    setLoading(prev => ({ ...prev, orders: true }));
    try {
      const apiUrl = `${v2ReactURL}/native/delivery/salesOrderList?contactId=${formData.contactVo.contactId}&deliveryDate=${formData.deliveryDate}&deliverySlotId=${formData.deliverySlotId}`;
      const response = await axiosFunction(apiUrl, "POST", false);
      
      if (response?.data?.status && response.data.response) {
        const salesOrdersData = response.data.response;
        
        if (isEditMode || isViewMode) {
          setSalesOrders(salesOrdersData);
          setSelectedOrders(salesOrdersData);
          setSelectAllOrders(true);
          
          salesOrdersData.forEach((order: SalesOrder) => {
            callBarcodeAPI(order.sales_id.toString());
          });
        } else {
          setSalesOrders(salesOrdersData);
          setSelectedOrders([]);
          setSelectAllOrders(false);
          setInventoryData([]);
        }
      } else {
        setSalesOrders([]);
        toastMessage("No sales orders for the selected criteria");
      }
    } catch (error) {
      console.error("Error fetching sales data:", error);
      setSalesOrders([]);
      toastMessage("No sales orders for the selected criteria");
    } finally {
      setLoading(prev => ({ ...prev, orders: false }));
    }
  };

  // Handle order selection
  const handleOrderSelect = (
    e: React.ChangeEvent<HTMLInputElement>,
    order: SalesOrder
  ) => {
    if (isViewMode) return;
    
    const isChecked = e.target.checked;

    const updatedSalesOrders = salesOrders.map(item => 
      item.sales_id === order.sales_id 
        ? { ...item, checked: isChecked ? 1 : 0 }
        : item
    );
    
    setSalesOrders(updatedSalesOrders);

    if (isChecked) {
      setSelectedOrders((prev) => [...prev, order]);
      callBarcodeAPI(order.sales_id.toString());
    } else {
      setSelectedOrders((prev) =>
        prev.filter((item) => item.sales_id !== order.sales_id)
      );
      setInventoryData((prev) =>
        prev.filter((item) => item.salesId !== order.sales_id.toString())
      );
    }

    const allChecked = updatedSalesOrders.every(order => order.checked === 1);
    setSelectAllOrders(allChecked);
  };

  // Handle select all orders
  const handleSelectAllOrders = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isViewMode) return;
    
    const isChecked = e.target.checked;
    setSelectAllOrders(isChecked);

    const updatedSalesOrders = salesOrders.map(order => ({
      ...order,
      checked: isChecked ? 1 : 0
    }));
    
    setSalesOrders(updatedSalesOrders);

    if (isChecked) {
      setSelectedOrders(salesOrders);
      salesOrders.forEach((order) => callBarcodeAPI(order.sales_id.toString()));
    } else {
      setSelectedOrders([]);
      setInventoryData([]);
    }
  };

  // Fetch barcode data for selected orders - UPDATED TO KEEP SEPARATE BY SALES_ID
  const callBarcodeAPI = async (salesId: string) => {
    setLoading(prev => ({ ...prev, inventory: true }));
    try {
      const response = await axiosFunction(
        `${v2ReactURL}/native/delivery/getProductBySales/${salesId}`,
        "POST",
        {},
        false
      );

      if (response?.data?.status && response.data.response && Array.isArray(response.data.response)) {
        const enrichedData = response.data.response.map((item: any) => ({
          salesId: salesId,
          sales_id: parseInt(salesId), // Add numeric sales_id for payload
          orderno: item.orderno || "N/A", // FIXED: Added orderno mapping
          item_code: item.item_code || "N/A",
          productVarientsVo: {
            varientName: item.varient_name || "N/A",
            productVo: {
              displayName: item.name || "N/A",
              brandVo: {
                brandName: "N/A"
              }
            }
          },
          qty: item.qty || 0,
          mrp: item.mrp,
          price: item.price,
          tax_name: item.tax_name,
          batch_no: item.batch_no,
          product_varient_id: item.product_varient_id,
          scannedQty: item.delvieryscanqty || 0, // Initialize scanned quantity to 0
          verified: item.delvieryscanqty === item.qty,// Initialize verification status to false
        }));
        
        setInventoryData((prev) => {
          const filteredPrev = prev.filter(item => item.salesId !== salesId);
          return [...filteredPrev, ...enrichedData];
        });
      }
    } catch (error) {
      console.error(`Error fetching barcode data for Sales ID ${salesId}:`, error);
    } finally {
      setLoading(prev => ({ ...prev, inventory: false }));
    }
  };

  // Create delivery payload - UPDATED TO MATCH YOUR REQUIREMENTS
  const createDeliveryPayload = (): DeliveryPayload => {
    const salesIds = selectedOrders.map(order => order.sales_id);
    
    // Create scannedQuantities array with separate entries for each sales_id
    const scannedQuantities: ScannedQuantityItem[] = inventoryData.map(item => ({
      productVarientId: item.product_varient_id || 0,
      expectedQty: item.qty,
      scannedQty: item.scannedQty || 0,
      salesId: item.sales_id || 0
    }));

    return {
      salesIds: salesIds,
      deliveryDate: formData.deliveryDate,
      contactId: parseInt(formData.contactVo.contactId),
      vehicleNo: vehicleNo || "123",
      deliverySlotId: formData.deliverySlotId,
      deliveryAssignId: isEditMode ? parseInt(localStorage.getItem('deliverySheetId') || "0") : 0,
      scannedQuantities: scannedQuantities
    };
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (isViewMode) {
      navigate("/delivery");
      return;
    }
    
    // Validation
    if (!formData.contactVo.contactId) {
      toastMessage("Please select a driver");
      return;
    }
    
    if (!formData.deliverySlotId) {
      toastMessage("Please select a delivery slot");
      return;
    }
    
    if (selectedOrders.length === 0) {
      toastMessage("Please select at least one sales order");
      return;
    }

    // Check if all items are verified
    const unverifiedItems = inventoryData.filter(item => !item.verified);
    if (unverifiedItems.length > 0) {
      const shouldProceed = window.confirm(
        `${unverifiedItems.length} items are not fully verified. Do you want to proceed anyway?`
      );
      if (!shouldProceed) {
        return;
      }
    }

    try {
      const payload = createDeliveryPayload();
      
      console.log("Delivery Payload:", payload); // For debugging

      let response;
      if (isEditMode && id) {
        response = await axiosFunction(
          `${v2ReactURL}/native/delivery/updateDeliverySheet/${id}`,
          "PUT",
          payload
        );
        
        if (response?.data?.status) {
          localStorage.removeItem(`deliverySheet_${id}`);
        }
      } else {
        response = await axiosFunction(
          `${v2ReactURL}/native/delivery/createDeliverySheet`,
          "POST",
          payload
        );
      }

      if (response?.data?.status) {
        toastMessage(`Delivery sheet ${isEditMode ? 'updated' : 'created'} successfully`);
        navigate("/delivery");
      } else {
        toastMessage(response?.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} delivery sheet`);
      }
    } catch (error: any) {
      console.error(`Error ${isEditMode ? 'updating' : 'saving'} delivery:`, error);
      toastMessage(error?.response?.data?.message || `Error ${isEditMode ? 'updating' : 'creating'} delivery sheet`);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    navigate("/delivery");
    localStorage.setItem('IsEdit', "False");
    localStorage.setItem('IsView', "False");
  };

  // Format phone number for display
  const formatPhoneNumber = (phone: string | null) => {
    if (!phone) return "N/A";
    return phone;
  };

  // Format customer name for display
  const formatCustomerName = (name: string) => {
    return name?.trim() || "N/A";
  };

  // Calculate verification statistics
  const verifiedItemsCount = inventoryData.filter(item => item.verified).length;
  const totalItemsCount = inventoryData.length;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div className="m-page--fluid m--skin- m-content--skin-light2 m-header--fixed m-header--fixed-mobile m-aside-left--enabled m-aside-left--skin-dark m-aside-left--fixed m-aside-left--offcanvas m-footer--push m-aside--offcanvas-default">
        <form
          className="m-form m-form--state m-form--fit m-form--label-align-left"
          id="recipe_form"
          onSubmit={handleSubmit}
        >
          <div className="m-grid m-grid--hor m-grid--root m-page">
            <div className="m-grid__item m-grid__item--fluid m-grid m-grid--ver-desktop m-grid--desktop m-body">
              <div className="m-grid__item m-grid__item--fluid m-wrapper">
                <div className="m-subheader ">
                  <div className="d-flex align-items-center">
                    <div className="mr-auto">
                      <h3 className="m-subheader__title m-subheader__title--separator">
                        {isViewMode ? 'View' : isEditMode ? 'Edit' : 'New'} {displayType}
                        {isEditMode && id && ` (#${id})`}
                      </h3>
                      <ul className="m-subheader__breadcrumbs m-nav m-nav--inline">
                        <li className="m-nav__item m-nav__item--home">
                          <a href="/" className="m-nav__link m-nav__link--icon">
                            <i className="m-nav__link-icon la la-home"></i>
                          </a>
                        </li>
                        <li className="m-nav__separator">-</li>
                        <li className="m-nav__item">
                          <a href={`/${type}`} className="m-nav__link">
                            <span className="m-nav__link-text">
                              {displayType}
                            </span>
                          </a>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="m-content">
                  {/* Loading indicator for edit/view mode */}
                  {(isEditMode || isViewMode) && loading.editData && (
                    <div className="text-center p-3">
                      <div className="spinner-border spinner-border-sm" role="status">
                        <span className="sr-only">Loading...</span>
                      </div>
                      <span className="ml-2">Loading delivery sheet data...</span>
                    </div>
                  )}

                  {/* Hidden fields */}
                  <input type="hidden" name="recipeId" value={formData.recipeId} />
                  <input type="hidden" name="taxType" value={formData.taxType} />
                  <input type="hidden" name="total" value={formData.total} />
                  <input type="hidden" name="saveandpayment" value={formData.saveandpayment} />
                  <input type="hidden" name="saveandnew" value={formData.saveandnew} />
                  <input type="hidden" name="saveandprint" value={formData.saveandprint} />

                  {/* Driver and Basic Information Section */}
                  <div className="row">
                    <div className="col-md-12">
                      <div className="m-portlet">
                        <div className="m-portlet__body card-body-sm">
                          <div className="row">
                            {/* Driver Selection */}
                            <div className="col-md-3">
                              <div className="form-group row">
                                <label className="col-md-12">
                                  Driver Name
                                  <small className="text-danger">*</small>
                                </label>
                                <div className="col-lg-12">
                                  <select
                                    className="form-control form-control-sm"
                                    value={formData.contactVo.contactId}
                                    onChange={handleDriverChange}
                                    disabled={isDisabled || loading.drivers}
                                  >
                                    <option value="">Select Driver</option>
                                    {drivers.map((driver) => (
                                      <option 
                                        key={driver.employeeId} 
                                        value={driver.employeeId.toString()}
                                      >
                                        {driver.employeeName} ({driver.mobileNo})
                                      </option>
                                    ))}
                                  </select>
                                  {loading.drivers && (
                                    <small className="text-muted">Loading drivers...</small>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Delivery Date with Calendar Picker */}
                            <div className="col-md-2">
                              <div className="form-group row">
                                <label className="col-md-12">
                                  {displayType} Date
                                  <small className="text-danger">*</small>
                                </label>
                                <div className="col-md-12">
                                  <DatePicker
                                    value={deliveryDate}
                                    onChange={handleDateChange}
                                    format="DD/MM/YYYY"
                                    slotProps={{
                                      textField: {
                                        size: "small",
                                        fullWidth: true,
                                        placeholder: "dd/mm/yyyy",
                                        disabled: isDisabled
                                      }
                                    }}
                                  />
                                </div>
                              </div>
                            </div>

                            {/* Delivery Slot */}
                            <div className="col-md-3">
                              <div className="form-group row">
                                <label className="col-md-12">
                                  Delivery Slot
                                  <small className="text-danger">*</small>
                                </label>
                                <div className="col-md-12">
                                  <select
                                    className="form-control form-control-sm"
                                    value={formData.deliverySlotId}
                                    onChange={handleDeliverySlotChange}
                                    disabled={isDisabled || loading.slots}
                                  >
                                    <option value="">Select Delivery Slot</option>
                                    {deliverySlots.map((slot) => (
                                      <option key={slot.deliverySlotId} value={slot.deliverySlotId.toString()}>
                                        {slot.deliverySlotName} ({slot.startTime} - {slot.endTime})
                                      </option>
                                    ))}
                                  </select>
                                  {loading.slots && (
                                    <small className="text-muted">Loading slots...</small>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Vehicle Selection */}
                            <div className="col-md-2">
                              <div className="form-group row">
                                <label className="col-md-12">Vehicle No</label>
                                <div className="col-md-12">
                                  <div className="input-group">
                                    <input
                                      type="text"
                                      className="form-control"
                                      name="vehicleNo"
                                      value={vehicleNo}
                                      readOnly
                                      disabled
                                      placeholder="Vehicle Number"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Sales Orders Section */}
                  <div className="row">
                    <div className="col-md-12">
                      <div className="m-portlet">
                        <div className="m-portlet__head m-portlet__head__sm">
                          <div className="m-portlet__head-tools d-flex align-items-center">
                            <ul
                              className="nav nav-tabs m-tabs-line m-tabs-line--primary m-tabs-line--2x"
                              role="tablist"
                            >
                              <li className="nav-item m-tabs__item">
                                <a
                                  className="nav-link m-tabs__link active"
                                  data-toggle="tab"
                                  href="#parent_table"
                                  role="tab"
                                >
                                  Sales Orders ({salesOrders.length})
                                </a>
                              </li>
                            </ul>

                            <div
                              className="form-group"
                              style={{ marginBottom: "17px", marginLeft: "10px" }}
                            >
                              <label className="m-checkbox m-checkbox--solid m-checkbox--brand m-0">
                                <input
                                  type="checkbox"
                                  id="selectAllOrders"
                                  checked={selectAllOrders}
                                  onChange={handleSelectAllOrders}
                                  disabled={isDisabled || salesOrders.length === 0 || loading.orders}
                                />{" "}
                                Select All Orders{" "}
                                <span style={{ marginTop: "16px" }}></span>
                              </label>
                            </div>
                          </div>
                        </div>
                        <div className="m-portlet__body card-body-sm">
                          <div className="tab-content">
                            <div className="tab-pane active">
                              <div className="row">
                                <div className="col-md-12">
                                  <div className="table-responsive">
                                    {loading.orders ? (
                                      <div className="text-center p-3">
                                        <div className="spinner-border spinner-border-sm" role="status">
                                          <span className="sr-only">Loading...</span>
                                        </div>
                                        <span className="ml-2">Loading sales orders...</span>
                                      </div>
                                    ) : (
                                      <table
                                        id="salesOrdersTable"
                                        className="table table-sm table-bordered table-striped mb-0"
                                      >
                                        <thead>
                                          <tr>
                                            <th>Select</th>
                                            <th>Sales No</th>
                                            <th>Customer Name</th>
                                            <th>Phone No</th>
                                            <th>Pincode</th>
                                            <th>Status</th>
                                            {/* <th>Payment</th> */}
                                            {/* <th>Total Items</th> */}
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {salesOrders.map((order) => (
                                            <tr key={order.sales_id}>
                                              <td>
                                                <input
                                                  type="checkbox"
                                                  name="selectOrder"
                                                  value={order.sales_id}
                                                  checked={order.checked === 1}
                                                  onChange={(e) => handleOrderSelect(e, order)}
                                                  disabled={isDisabled || loading.inventory}
                                                />
                                              </td>
                                             <td
  style={{ cursor: "pointer", color: "#1976d2", textDecoration: "underline" }}
  onClick={() => window.location.href = `${window.location.origin}/pos/${order.sales_no}`}
>
  <strong>{order.prefix}.{order.sales_no}</strong>
</td>

                                              <td>{formatCustomerName(order.fullname)}</td>
                                              <td>{formatPhoneNumber(order.phoneno)}</td>
                                              <td>{order.pincode}</td>
                                              <td>
                                                <span className={`badge ${
                                                  order.status === 'open' ? 'badge-success' : 'badge-warning'
                                                }`}>
                                                  {order.status}
                                                </span>
                                              </td>
                                              {/* <td>
                                                <span className={`badge ${
                                                  order.payment_status === 'Due' ? 'badge-danger' : 'badge-success'
                                                }`}>
                                                  {order.payment_status}
                                                </span>
                                              </td> */}
                                              {/* <td>
                                                {order?.salesItems?.length} - items
                                              </td> */}
                                            </tr>
                                          ))}
                                          {salesOrders.length === 0 && (
                                            <tr>
                                              <td colSpan={8} className="text-center">
                                                {formData.contactVo.contactId && formData.deliverySlotId && formData.deliveryDate 
                                                  ? "No sales orders found for the selected criteria" 
                                                  : "Please select a driver, delivery date, and delivery slot to view orders"}
                                              </td>
                                            </tr>
                                          )}
                                        </tbody>
                                      </table>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Inventory Management Section - UPDATED FOR SEPARATE SALES_ID HANDLING */}
                  <div className="row">
                    <div className="col-md-12">
                      <div className="m-portlet">
                        <div className="m-portlet__head m-portlet__head__sm">
                          <div className="m-portlet__head-tools">
                            <ul
                              className="nav nav-tabs m-tabs-line m-tabs-line--primary m-tabs-line--2x"
                              role="tablist"
                            >
                              <li className="nav-item m-tabs__item">
                                <a
                                  className="nav-link m-tabs__link active"
                                  data-toggle="tab"
                                  href="#inventoryTable"
                                  role="tab"
                                >
                                  Inventory Management 
                                  {inventoryData.length > 0 && ` (${verifiedItemsCount}/${totalItemsCount} verified)`}
                                </a>
                              </li>
                            </ul>
                          </div>
                        </div>
                        <div className="m-portlet__body card-body-sm">
                          {/* Scanner Input Section */}
                          {
                            switchProductScanning === 1 && (
    <div className="row mb-4">
                            <div className="col-md-6">
                              <div className="form-group">
                                <label className="form-label">
                                  Enter Item Code
                                  <small className="text-danger">*</small>
                                </label>
                                <div className="input-group">
                                  <input
                                    ref={scannerInputRef}
                                    type="text"
                                    className="form-control"
                                    placeholder="Scan barcode or type item code..."
                                    value={scannerInput}
                                    onChange={handleScannerInput}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleManualSearch();
                                      }
                                    }}
                                    disabled={loading.productSearch || isViewMode}
                                  />
                                
                                </div>
                                <small className="form-text text-muted">
                                  Scan barcode or enter item code to update scanned quantity
                                </small>
                              </div>
                            </div>
                          </div>
                            )
                          }
                      

                          <div className="tab-content">
                            <div className="tab-pane active">
                              <div className="row">
                                <div className="col-md-12">
                                  <div className="table-responsive">
                                    {loading.inventory ? (
                                      <div className="text-center p-3">
                                        <div className="spinner-border spinner-border-sm" role="status">
                                          <span className="sr-only">Loading...</span>
                                        </div>
                                        <span className="ml-2">Loading inventory data...</span>
                                      </div>
                                    ) : (
                                      <table
                                        id="inventory_table"
                                        className="table table-sm table-bordered table-striped mb-0"
                                      >
                                        <thead>
                                          <tr>
                                            <th>#</th>
                                            <th>Order No</th>
                                            <th>Product Name</th>
                                            {/* <th>Variant ID</th> */}
                                            <th>Batch No</th>
                                            <th>Item Code</th>
                                            <th>Total Qty</th>
                                            {
                                              switchProductScanning === 1 ? <th>Scanned Qty</th> : null
                                            }
                                            {
                                              switchProductScanning === 1 ? <th>Verify</th> : null
                                            }
                                      
                                            <th>Price</th>
                                            <th>MRP</th>
                                            <th>Total</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {inventoryData.map((item, index) => (
                                            <tr key={`${item.salesId}-${item.item_code}-${index}`}>
                                              <td>{index + 1}</td>
                                              <td>
                                                <strong>{item.orderno}</strong>
                                              </td>
                                              <td>
                                                {item.productVarientsVo?.productVo?.displayName || "N/A"}
                                              </td>
                                              {/* <td>
                                                {item.product_varient_id || "N/A"}
                                              </td> */}
                                              <td>
                                                {item.batch_no || "N/A"}
                                              </td>
                                              <td>
                                                <code>{item.item_code || "N/A"}</code>
                                              </td>
                                              <td>{item.qty || 0}</td>
                                        <td>
  {switchProductScanning === 1 ? (
    isViewMode ? (
      <span>{item.scannedQty || 0}</span>
    ) : (
      <input
        type="number"
        className="form-control form-control-sm"
        value={item.scannedQty || 0}
        onChange={(e) => handleInventoryQuantityInput(
          item.item_code, 
          item.sales_id || 0, 
          parseInt(e.target.value) || 0
        )}
        min="0"
        max={item.qty}
        style={{ width: '80px' }}
      />
    )
  ) : (
    <span>{item.scannedQty || 0}</span>
  )}
</td>
{switchProductScanning === 1 && (
  <td className="text-center">
    {item.verified ? (
      <span style={{color: "#008000", fontSize: '22px'}}>
        <FaCheckSquare />
      </span>
    ) : (
      <span style={{color: colors.orange[500], fontSize: '22px'}}>
        <IoIosWarning />
      </span>
    )}
  </td>
)}
                                              <td>₹{item.price || 0}</td>
                                              <td>₹{item.mrp || 0}</td>
                                              <td>₹{((item.price || 0) * (item.scannedQty || 0)).toFixed(2)}</td>
                                            </tr>
                                          ))}
                                          {inventoryData.length === 0 && (
                                            <tr>
                                              <td colSpan={12} className="text-center text-muted">
                                                {selectedOrders.length > 0 
                                                  ? "No inventory data found for selected orders" 
                                                  : "No inventory items selected. Please select sales orders above to view inventory."}
                                              </td>
                                            </tr>
                                          )}
                                        </tbody>
                                        {inventoryData.length > 0 && (
                                          <tfoot>
                                            <tr className="bg-light">
                                              <td colSpan={6} className="text-right"><strong>Total:</strong></td>
                                              <td><strong>{inventoryData.reduce((sum, item) => sum + item.qty, 0)}</strong></td>
                                              <td><strong>{inventoryData.reduce((sum, item) => sum + (item.scannedQty || 0), 0)}</strong></td>
                                              <td colSpan={2}></td>
                                              <td><strong>₹{inventoryData.reduce((sum, item) => sum + ((item.price || 0) * (item.scannedQty || 0)), 0).toFixed(2)}</strong></td>
                                              <td>
                                                <strong>
                                                  {verifiedItemsCount}/{totalItemsCount}
                                                </strong>
                                              </td>
                                            </tr>
                                          </tfoot>
                                        )}
                                      </table>
                                    )}
                                  </div>
                                  
                                  {/* API Call Status */}
                                  {selectedOrders.length > 0 && (
                                    <div className="mt-2">
                                      <small className="text-muted">
                                        Showing data from {new Set(inventoryData.map(item => item.salesId)).size} out of {selectedOrders.length} selected orders
                                      </small>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <footer
              className="m-grid__item m-footer"
              style={{
                minHeight: "45px",
                height: "45px",
                position: "fixed",
                bottom: 0,
                right: 0,
                left: 0,
                backgroundColor: "#EEF0F8",
              }}
            >
              <div
                className="m-container m-container--fluid m-container--full-height m-page__container"
                style={{ display: "flex" }}
              >
                <div
                  style={{
                    justifyContent: "flex-start",
                    flex: "1 1 0",
                    display: "flex",
                    whiteSpace: "nowrap",
                  }}
                >
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="btn btn-sm btn-secondary"
                  >
                    {isViewMode ? 'Back' : 'Cancel'}
                  </button>
                </div>
                <div
                  style={{
                    justifyContent: "flex-end",
                    flex: "1 1 0",
                    display: "flex",
                    whiteSpace: "nowrap",
                  }}
                >
                 {!isViewMode && (
  <button
    type="submit"
    className="btn btn-sm btn-primary"
    disabled={
       inventoryData.some(item => !item.verified) ||
      selectedOrders.length === 0 || 
      !formData.contactVo.contactId || 
      !formData.deliverySlotId || 
      isDisabled
    }
  >
    {isEditMode ? 'Update' : 'Save'} Delivery 
    ({selectedOrders.length} orders, {verifiedItemsCount}/{totalItemsCount} verified)
  </button>
)}
                </div>
              </div>
            </footer>
          </div>
        </form>
      </div>
    </LocalizationProvider>
  );
};

export default DeliveryForm;
